import React, { useState, useEffect } from 'react';
import { Outlet, NavLink, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useFeatures } from '../context/FeatureContext';
import { resolveAssetUrl } from '../services/api';
import { TrendingUp, Briefcase, Trophy, LogOut, Users, Radio, UserPlus, Heart, User, ChevronDown, Brain, BookOpen } from 'lucide-react';
import logoImage from '../assets/logo.png';
import faviconImage from '../assets/favicon.png';

export function IPLTradeLogo({ size = 28 }) {
  return (
    <img src={logoImage} alt="IPLTrade" style={{ width: size, height: 'auto', flexShrink: 0, objectFit: 'contain', display: 'block' }} />
  );
}

function UserAvatar({ user, size = 32 }) {
  const name = user?.display_name || user?.username || '?';
  const initials = name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
  const colors = ['#f59e0b','#10b981','#3b82f6','#8b5cf6','#ef4444','#ec4899'];
  const color = colors[name.charCodeAt(0) % colors.length];

  if (user?.avatar_url && user.avatar_url !== 'none') {
    return (
      <img
        src={resolveAssetUrl(user.avatar_url)}
        alt={name}
        style={{ width: size, height: size, borderRadius: '50%', objectFit: 'cover', flexShrink: 0, border: `2px solid ${color}50` }}
        onError={e => { e.target.style.display = 'none'; }}
      />
    );
  }
  return (
    <div style={{
      width: size, height: size, borderRadius: '50%', flexShrink: 0,
      background: color + '25', color, border: `2px solid ${color}50`,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontFamily: 'var(--font-display)', fontWeight: 700,
      fontSize: size > 36 ? '0.9rem' : '0.72rem',
      cursor: 'pointer',
    }}>
      {initials}
    </div>
  );
}

export default function Layout() {
  const { user, logout } = useAuth();
  const feat = useFeatures();
  const navigate = useNavigate();
  const [userMenuOpen, setUserMenuOpen] = useState(false);

  // Replace all favicon links with the webpack-bundled src/assets/favicon.png.
  // We remove and recreate (not just update href) to force the browser to re-fetch.
  useEffect(() => {
    document.querySelectorAll("link[rel~='icon']").forEach(el => el.parentNode?.removeChild(el));
    ['icon', 'shortcut icon', 'apple-touch-icon'].forEach(rel => {
      const link = document.createElement('link');
      link.rel  = rel;
      link.type = 'image/png';
      link.href = faviconImage;
      document.head.appendChild(link);
    });
  }, []);
  const handleLogout = () => { logout(); navigate('/auth'); };
  const displayName = user?.display_name || user?.username || '';

  const pts        = user ? parseFloat(user.points).toFixed(0) : '0';
  const tradesLeft = user?.trades_remaining ?? ((user?.max_trades || 100) - (user?.trade_count || 0));
  const tradesLow  = tradesLeft <= 10;
  const tradesDone = tradesLeft <= 0;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>

      <nav className="navbar">
        <Link to="/market" className="navbar-brand" style={{ display: 'flex', alignItems: 'center', textDecoration: 'none' }}>
          <IPLTradeLogo size={108} />
        </Link>

        <div className="nav-links">
          {feat.market       && <NavLink to="/market" end className={({ isActive }) => 'nav-link' + (isActive ? ' active' : '')}><TrendingUp size={15} /> Market</NavLink>}
          {feat.portfolio    && <NavLink to="/market/portfolio"    className={({ isActive }) => 'nav-link' + (isActive ? ' active' : '')}><Briefcase size={15} /> Portfolio</NavLink>}
          {feat.contests     && <NavLink to="/market/contests"     className={({ isActive }) => 'nav-link' + (isActive ? ' active' : '')}><Users size={15} /> Contests</NavLink>}
          {feat.feed         && <NavLink to="/market/feed"         className={({ isActive }) => 'nav-link' + (isActive ? ' active' : '')}><Radio size={15} /> Feed</NavLink>}
          {feat.intelligence && <NavLink to="/market/intelligence" className={({ isActive }) => 'nav-link' + (isActive ? ' active' : '')}><Brain size={15} /> Intel</NavLink>}
          {feat.leaderboard  && <NavLink to="/market/leaderboard"  className={({ isActive }) => 'nav-link' + (isActive ? ' active' : '')}><Trophy size={15} /> Leaderboard</NavLink>}
          <NavLink to="/market/rules" className={({ isActive }) => 'nav-link' + (isActive ? ' active' : '')}><BookOpen size={15} /> Rules</NavLink>
        </div>

        <div className="nav-user">
          <span
            title={`${tradesLeft} of ${user?.max_trades || 200} season trades remaining`}
            style={{
              fontSize: '0.7rem', fontWeight: 600, padding: '3px 9px', borderRadius: 20, cursor: 'default',
              border: `1px solid ${tradesDone ? 'rgba(239,68,68,0.4)' : tradesLow ? 'rgba(245,158,11,0.4)' : 'rgba(148,163,184,0.2)'}`,
              background: tradesDone ? 'rgba(239,68,68,0.1)' : tradesLow ? 'rgba(245,158,11,0.1)' : 'transparent',
              color: tradesDone ? 'var(--red)' : tradesLow ? 'var(--accent)' : 'var(--text-muted)',
            }}
          >
            🔁 {tradesLeft}
          </span>
          <span className="points-badge">⬡ {pts} pts</span>

          {/* User avatar dropdown */}
          <div style={{ position: 'relative' }}>
            <div
              onClick={() => setUserMenuOpen(o => !o)}
              style={{ display: 'flex', alignItems: 'center', gap: 5, cursor: 'pointer', padding: '3px 6px', borderRadius: 8, transition: 'background 0.15s' }}
              onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-elevated)'}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
            >
              <UserAvatar user={user} size={30} />
              <ChevronDown size={12} color="var(--text-muted)" />
            </div>

            {userMenuOpen && (
              <>
                <div onClick={() => setUserMenuOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 99 }} />
                <div style={{
                  position: 'absolute', right: 0, top: '100%', marginTop: 6,
                  background: 'var(--bg-card)', border: '1px solid var(--border)',
                  borderRadius: 12, minWidth: 180, zIndex: 100, overflow: 'hidden',
                  boxShadow: '0 8px 24px rgba(0,0,0,0.3)',
                }}>
                  <div style={{ padding: '12px 14px', borderBottom: '1px solid var(--border)' }}>
                    <div className="truncate-1" style={{ fontWeight: 600, fontSize: '0.87rem', maxWidth: 150 }}>{displayName}</div>
                    <div className="truncate-1" style={{ fontSize: '0.7rem', color: 'var(--text-muted)', maxWidth: 150 }}>@{user?.username}</div>
                  </div>
                  {[
                    feat.profile  && { to: '/market/profile', icon: <User size={13} />,     label: 'My Profile' },
                    feat.friends  && { to: '/market/friends', icon: <UserPlus size={13} />,  label: 'Friends' },
                                      { to: '/market/rules',   icon: <BookOpen size={13} />, label: 'Rule Book' },
                    feat.donate   && { to: '/market/donate',  icon: <Heart size={13} />,     label: 'Support Us ☕' },
                  ].filter(Boolean).map(item => (
                    <Link
                      key={item.to}
                      to={item.to}
                      onClick={() => setUserMenuOpen(false)}
                      style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', color: 'var(--text-secondary)', textDecoration: 'none', fontSize: '0.84rem', transition: 'background 0.1s' }}
                      onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-secondary)'}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                    >
                      {item.icon} {item.label}
                    </Link>
                  ))}
                  <div style={{ borderTop: '1px solid var(--border)' }}>
                    <button
                      onClick={handleLogout}
                      style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', color: 'var(--red)', background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.84rem', textAlign: 'left' }}
                      onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-secondary)'}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                    >
                      <LogOut size={13} /> Logout
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </nav>

      <main className="main-content" style={{ flex: 1 }}>
        <Outlet />
      </main>

      <footer className="site-footer" style={{
        borderTop: '1px solid var(--border)', background: 'var(--bg-primary)',
        padding: '12px 1.5rem', display: 'flex', justifyContent: 'space-between',
        alignItems: 'center', flexWrap: 'wrap', gap: 8,
        fontSize: '0.73rem', color: 'var(--text-muted)', flexShrink: 0,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
          <IPLTradeLogo size={16} />
          <span>© 2026 IPLTrade.in — Free fantasy cricket trading. Not affiliated with BCCI or IPL.</span>
        </div>
        <div style={{ display: 'flex', gap: 14, alignItems: 'center' }}>
          {feat.donate && (
            <Link to="/market/donate" style={{ color: 'var(--accent)', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 4 }}>
              <Heart size={11} /> Support
            </Link>
          )}
          <Link to="/market/rules"       style={{ color: 'var(--text-muted)', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 4 }}><BookOpen size={11} /> Rules</Link>
          <Link to="/declaration" style={{ color: 'var(--text-muted)', textDecoration: 'none' }}>Legal</Link>
          <Link to="/developer"   style={{ color: 'var(--text-muted)', textDecoration: 'none' }}>Developer</Link>
        </div>
      </footer>

      {/* Mobile bottom nav */}
      <nav className="bottom-nav">
        <div className="bottom-nav-inner">
          {feat.market       && <NavLink to="/market" end className={({ isActive }) => 'bottom-nav-item' + (isActive ? ' active' : '')}><TrendingUp /><span>Market</span></NavLink>}
          {feat.portfolio    && <NavLink to="/market/portfolio"    className={({ isActive }) => 'bottom-nav-item' + (isActive ? ' active' : '')}><Briefcase /><span>Portfolio</span></NavLink>}
          {feat.contests     && <NavLink to="/market/contests"     className={({ isActive }) => 'bottom-nav-item' + (isActive ? ' active' : '')}><Users /><span>Contests</span></NavLink>}
          {feat.intelligence && <NavLink to="/market/intelligence" className={({ isActive }) => 'bottom-nav-item' + (isActive ? ' active' : '')}><Brain /><span>Intel</span></NavLink>}
          {feat.leaderboard  && <NavLink to="/market/leaderboard"  className={({ isActive }) => 'bottom-nav-item' + (isActive ? ' active' : '')}><Trophy /><span>Leaders</span></NavLink>}
          {feat.profile      && <Link to="/market/profile" className="bottom-nav-item"><User /><span>Profile</span></Link>}
          <NavLink to="/market/rules" className={({ isActive }) => 'bottom-nav-item' + (isActive ? ' active' : '')}><BookOpen /><span>Rules</span></NavLink>
        </div>
      </nav>
    </div>
  );
}
